declare module "react" {
  interface CSSProperties {
    //   Add custom CSS var definitions
    "--page_size"?: number;
    "--visible-columns-count"?: number;
  }
}
import type {
  CellContext,
  ColumnFiltersState,
  ColumnDef,
  HeaderContext,
  InitialTableState,
  OnChangeFn,
  PaginationState,
  RowData,
  TableOptions,
  Table as TanstackTable,
} from "@tanstack/react-table";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  Dispatch,
  forwardRef,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  Ref,
  SetStateAction,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { IsoCode } from "../../contexts/exchange-rates";
import { cn } from "../../utils/classes";
import { RelatedListSearch } from "../related-list";
import { TableBody } from "./body";
import { TableHead } from "./head";
import styles from "./table.module.css";
import { Pagination, PaginationOptions } from "./utils/pagination";
export { CurrencyCell } from "./body/cell/currency";
export { DateTimeCell } from "./body/cell/date";
export { DocumentLabelCell, DocumentTypeCell } from "./body/cell/document";
export { EntityCell } from "./body/cell/entity";
export { TextCell } from "./body/cell/text";
export { UserCell } from "./body/cell/user";

export const PADDING_LEFT = "_padding_left";
export const PADDING_RIGHT = "_padding_right";

export type CellOrHeaderContext<D, V> = CellContext<D, V> | HeaderContext<D, V>;

export const isCellContext = <D, V>(
  info: CellOrHeaderContext<D, V>,
): info is CellContext<D, V> => typeof info === "object" && "row" in info;

export type ValueOrContextFn<D, V, T> =
  | T
  | ((info: CellOrHeaderContext<D, V>) => T);

export interface TableColumnFilterOption {
  label: string;
  value: string;
}

declare module "@tanstack/react-table" {
  interface ColumnMeta<TData extends RowData, TValue> {
    sticky?: "right" | "left";
    gridSize?: string;
    cellClass?: string;
    headerCellClass?: string;
    // Render the header content raw — without the default truncating, text-height
    // span — so it can fill the cell (full height + horizontal overflow visible).
    // Used by headers that draw their own full-height affordances (e.g. editable
    // reporting columns with drag/insert borders).
    rawHeader?: boolean;
    displayCurrency?: IsoCode;
    companyId?: string;
    grow?: boolean;
    align?: ValueOrContextFn<
      TData,
      TValue,
      "left" | "center" | "right" | undefined
    >;
    className?: string;
    filter?: {
      type: "multi-select";
      title?: string;
      options: TableColumnFilterOption[];
    };
  }
}

export type TableProps<D extends RowData> = PropsWithChildren<{
  columns: TableOptions<D>["columns"];
  defaultData: D[];
  className?: string;
  containerClassName?: string;
  pagination?: PaginationOptions;
  initialState?: InitialTableState;
  anchor?: boolean;
  virtual?: {
    columns?: boolean;
    rows?: boolean;
  };
  onEndReached?: () => void;
  scrollAreaLabel?: string;
  topOverlay?: ReactNode;
  endContent?: ReactNode;
  filtering?: {
    mode?: "client" | "server";
    columnFilters?: ColumnFiltersState;
    onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>;
  };
  // When provided, every body row becomes clickable (cursor + hover) and this
  // is called with the row's original data on click. Use for "open this row"
  // navigation instead of a per-row action button.
  onRowClick?: (row: D) => void;
  // By default `defaultData` only seeds the table's internal state (so callers
  // can drive updates imperatively via the `setData` ref). When `syncData` is
  // set, the table instead follows the `defaultData` prop on every render —
  // use it when the parent owns the data (e.g. a live SSE-updated list).
  syncData?: boolean;
}>;

export type Table<D extends RowData> = TanstackTable<D> & {
  setData: Dispatch<SetStateAction<D[]>>;
};

// ToDo: Fix hardcoded generic types
export const Table = forwardRef<Table<RowData>, TableProps<RowData>>(
  (
    {
      columns: tableColumns,
      defaultData,
      className,
      containerClassName,
      children,
      anchor,
      pagination: paginationOptions,
      initialState,
      filtering,
      endContent,
      onEndReached,
      onRowClick,
      topOverlay,
      scrollAreaLabel,
      syncData,
      virtual = {
        columns: true,
      },
    },
    ref,
  ) => {
    const [internalData, setData] = useState(defaultData);
    // When `syncData` is set the parent owns the data: follow the prop directly
    // (render-derived, no effect) so live updates flow through. Otherwise the
    // table is driven imperatively via the `setData` ref.
    const data = syncData ? defaultData : internalData;
    const [internalColumnFilters, setInternalColumnFilters] =
      useState<ColumnFiltersState>([]);

    const pageSize = paginationOptions?.pageSize ?? 7;
    const manualPagination = paginationOptions?.mode === "server";
    const paginationState: PaginationState = {
      pageIndex: paginationOptions?.pageIndex ?? 0,
      pageSize,
    };
    const filteringMode = filtering?.mode ?? "client";
    const columnFilters = filtering?.columnFilters ?? internalColumnFilters;
    const onColumnFiltersChange =
      filtering?.onColumnFiltersChange ?? setInternalColumnFilters;

    const columns = useMemo<ColumnDef<RowData>[]>(
      () => tableColumns,
      [tableColumns],
    );

    const table = useReactTable({
      data,
      columns,
      columnResizeMode: "onChange",
      enableSortingRemoval: true,
      globalFilterFn: (row, columnId, filterValue) => {
        const relatedListSearch = filterValue?.[RelatedListSearch]?.trim();
        // TODO: Handle different data types for search rather than stringify
        if (
          relatedListSearch &&
          !JSON.stringify(row.getValue(columnId))
            .toLowerCase()
            .includes(filterValue?.[RelatedListSearch]?.toLowerCase())
        )
          return false;

        return true;
      },
      enableGlobalFilter: true,
      onColumnFiltersChange,
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel:
        filteringMode === "client" ? getFilteredRowModel() : undefined,
      getSortedRowModel: getSortedRowModel(),
      getPaginationRowModel:
        paginationOptions?.enabled && !manualPagination
          ? getPaginationRowModel()
          : undefined,
      manualPagination,
      ...(manualPagination
        ? {
            pageCount: paginationOptions?.pageCount,
            rowCount: paginationOptions?.rowCount,
            onPaginationChange: (
              updater:
                | PaginationState
                | ((old: PaginationState) => PaginationState),
            ) => {
              const nextState =
                typeof updater === "function"
                  ? updater(paginationState)
                  : updater;
              paginationOptions?.onPageChange?.(nextState.pageIndex);
            },
          }
        : {}),
      manualFiltering: filteringMode === "server",
      initialState: {
        ...initialState,
        ...(paginationOptions?.enabled
          ? {
              pagination: {
                pageIndex: 0,
                pageSize,
              },
            }
          : {}),
      },
      state: {
        columnFilters,
        ...(manualPagination ? { pagination: paginationState } : {}),
      },
    });

    useImperativeHandle(ref, () => ({ ...table, setData }));

    const parentRef = useRef<HTMLDivElement>(null);
    const tableRef = useRef<HTMLTableElement>(null);
    const [lockedColumnWidths, setLockedColumnWidths] = useState<{
      signature: string;
      values: Record<string, string>;
    }>();

    // TODO: Scroll to newest piece of data instead
    useEffect(() => {
      if (anchor && parentRef.current) {
        parentRef.current.scrollBy({
          left: parentRef.current.scrollWidth,
          behavior: "smooth",
        });
      }
    }, [data]);

    const visibleColumns = table.getVisibleLeafColumns();
    const columnSignature = visibleColumns
      .map(
        ({ id, columnDef }) => `${id}\u0001${columnDef.meta?.gridSize ?? ""}`,
      )
      .join("\u0000");

    const columnVirtualizer = useVirtualizer<
      HTMLDivElement,
      HTMLTableCellElement
    >({
      gap: 10,
      count: visibleColumns.length,
      estimateSize: (index) => visibleColumns[index].getSize(),
      getScrollElement: () => parentRef.current,
      horizontal: true,
      overscan: virtual.columns ? 10 : columns.length,
    });

    const virtualColumns = columnVirtualizer.getVirtualItems();
    const virtualColumnSignature = virtualColumns
      .map(({ index }) => visibleColumns[index]?.id)
      .join("\u0000");

    const virtualPaddingLeft =
      columnVirtualizer && virtualColumns?.length
        ? virtualColumns[0]?.start ?? 0
        : undefined;
    const virtualPaddingRight =
      columnVirtualizer && virtualColumns?.length
        ? columnVirtualizer.getTotalSize() -
          (virtualColumns[virtualColumns.length - 1]?.end ?? 0)
        : undefined;

    const gridTemplateColumns = useMemo(() => {
      const headers = table.getFlatHeaders();
      const activeLockedColumnWidths =
        lockedColumnWidths?.signature === columnSignature
          ? lockedColumnWidths.values
          : undefined;

      return columnVirtualizer
        .getVirtualItems()
        .map((col, idx, cols) => {
          const header = headers[col.index];
          const lockedWidth = activeLockedColumnWidths?.[header.column.id];

          if (lockedWidth) return lockedWidth;

          if (header.column.columnDef.meta?.gridSize)
            return header.column.columnDef.meta?.gridSize;

          const headerSize = header.getSize()
            ? `${header.getSize()}px`
            : `min-content`;

          if (header.column.columnDef.meta?.grow)
            return `minmax(${headerSize}, auto)`;

          return headerSize;
        })
        .join(" ");
    }, [
      columnSignature,
      columnVirtualizer.getVirtualItems(),
      lockedColumnWidths,
    ]);

    const shouldLockColumnWidths = Boolean(virtual.rows);
    const activeLockedColumnWidths =
      lockedColumnWidths?.signature === columnSignature
        ? lockedColumnWidths.values
        : {};

    useLayoutEffect(() => {
      if (
        !shouldLockColumnWidths ||
        data.length === 0 ||
        virtualColumns.length === 0 ||
        !tableRef.current
      ) {
        return;
      }

      const resolvedColumns = getComputedStyle(tableRef.current)
        .gridTemplateColumns.split(/\s+/)
        .filter(Boolean);
      if (resolvedColumns.length !== virtualColumns.length) return;

      const nextWidths = { ...activeLockedColumnWidths };
      let changed = false;

      virtualColumns.forEach((virtualColumn, index) => {
        const column = visibleColumns[virtualColumn.index];
        if (!nextWidths[column.id]) {
          nextWidths[column.id] = resolvedColumns[index];
          changed = true;
        }
      });

      if (changed) {
        setLockedColumnWidths({
          signature: columnSignature,
          values: nextWidths,
        });
      }
    }, [
      activeLockedColumnWidths,
      columnSignature,
      data.length,
      gridTemplateColumns,
      shouldLockColumnWidths,
      virtualColumnSignature,
      virtualColumns.length,
      visibleColumns,
    ]);

    return (
      <>
        <div
          ref={parentRef}
          aria-label={scrollAreaLabel}
          tabIndex={virtual.rows ? 0 : undefined}
          style={{
            height: "100%",
            width: "100%",
            scrollbarGutter: virtual.rows ? "stable" : undefined,
            // TODO: Investigate enchoring
            // ...(anchor ? { overflowAnchor: "auto" } : {}),
          }}
          className={cn(
            "inline-flex size-full flex-col align-middle overflow-y-auto",
            {
              "overflow-x-auto": !paginationOptions?.enabled,
            },
            containerClassName,
          )}
        >
          <table
            className={cn(
              styles.table,
              "grid table-fixed divide-y divide-gray-300",
              className,
            )}
            ref={tableRef}
            style={{
              gridTemplateColumns,
              columnGap: "10px",
              minWidth: "100%",
              width: "max-content",
              "--page_size": Math.min(pageSize, data.length),
              "--visible-columns-count":
                columnVirtualizer.getVirtualItems().length,
            }}
          >
            {topOverlay && (
              <caption
                aria-hidden
                className="pointer-events-none sticky top-0 z-20 col-[1/-1] h-0 overflow-visible"
              >
                {topOverlay}
              </caption>
            )}
            <TableHead
              columnVirtualizer={columnVirtualizer}
              table={table}
              virtualPaddingLeft={virtualPaddingLeft}
              virtualPaddingRight={virtualPaddingRight}
            />
            <TableBody
              columnVirtualizer={columnVirtualizer}
              table={table}
              tableContainerRef={parentRef}
              virtualPaddingLeft={virtualPaddingLeft}
              virtualPaddingRight={virtualPaddingRight}
              virtualRows={virtual.rows}
              endContent={endContent}
              onEndReached={onEndReached}
              onRowClick={onRowClick}
            >
              {children}
            </TableBody>
          </table>
        </div>
        {paginationOptions?.enabled && <Pagination table={table} />}
      </>
    );
  },
) as <D extends RowData>(
  p: TableProps<D> & { ref?: Ref<Table<D>> },
) => ReactElement;

export default Table;
